import { useCallback, useMemo, useState } from 'react';

/**
 * Row selection for a paginated list.
 *
 * Fourteen admin screens in the 2026-09-18 batch need "tick some rows, then do
 * something to them". The fiddly parts are the same every time and are easy to
 * get subtly wrong, so they are solved once here:
 *
 * - **Selection survives paging.** Ticking three rows on page 1 and three on
 *   page 2 acts on six. A naive implementation stores the current page's rows
 *   and silently drops the earlier ones -- the person then sees "3 deleted"
 *   after selecting six, with no hint which three.
 * - **"Select all" means all on THIS page**, never "all rows matching the
 *   filter", because the screen cannot show what it did not fetch. The header
 *   checkbox therefore reflects only the visible rows, and the count says how
 *   many are selected in total so the difference is visible.
 * - **Selecting a row that then disappears** (a filter change, a refetch) must
 *   not leave a ghost in the selection acting on something off-screen. Callers
 *   pass the ids currently visible and `visibleSelected` is what the action bar
 *   reports.
 */
export interface BulkSelection {
  /** Every id ticked, across pages. */
  selectedIds: string[];
  /** Ticked AND currently on screen. */
  visibleSelectedIds: string[];
  count: number;
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Tick or untick every row currently on screen. */
  toggleAllVisible: () => void;
  clear: () => void;
  /** Header checkbox: every visible row is ticked (and there is at least one). */
  allVisibleSelected: boolean;
  /** Header checkbox indeterminate state. */
  someVisibleSelected: boolean;
}

export function useBulkSelection(visibleIds: string[]): BulkSelection {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const visibleSelectedIds = useMemo(
    () => visibleIds.filter((id) => selected.has(id)),
    [visibleIds, selected]
  );

  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedIds.length === visibleIds.length;
  const someVisibleSelected = visibleSelectedIds.length > 0 && !allVisibleSelected;

  const toggleAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      const everyVisibleTicked =
        visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
      if (everyVisibleTicked) {
        // Untick only what is on screen; a selection made on another page is
        // not the header checkbox's business.
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [visibleIds]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  return {
    selectedIds,
    visibleSelectedIds,
    count: selected.size,
    isSelected: useCallback((id: string) => selected.has(id), [selected]),
    toggle,
    toggleAllVisible,
    clear,
    allVisibleSelected,
    someVisibleSelected,
  };
}
